import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Check, Loader2, Eye, EyeOff } from 'lucide-react';
import { API } from '../lib/api';
import { SignupDiscount } from '../components/driveline/SignupDiscount';

const PLANS = [
  { key: 'starter', name: 'Starter', price: 1499, yearly: 14990,
    blurb: 'Billing and customers for a single workshop',
    points: ['Customers & vehicles', 'Estimates and invoices', 'PDF & WhatsApp sharing', 'Up to 5 staff logins'] },
  { key: 'professional', name: 'Professional', price: 3999, yearly: 39990, featured: true,
    blurb: 'Everything a busy workshop runs on, day to day',
    points: ['Everything in Starter', 'Vehicle inspections with photos', 'Inventory & consumption', 'eWarranty certificates',
             'Payroll & attendance', 'GST invoicing & returns', 'Up to 25 staff logins'] },
  { key: 'enterprise', name: 'Enterprise', price: 7999, yearly: 79990,
    blurb: 'Multi-branch, with performance management and AI',
    points: ['Everything in Professional', 'Performance reviews & skill matrix', 'AI training plans & team analysis',
             'SOP training modules', 'Pre-owned marketplace', 'Unlimited staff logins'] },
];

const field = 'w-full bg-white border border-slate-300 rounded-xl px-4 py-3 text-base sm:text-sm ' +
  'text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-slate-900 focus:ring-2 focus:ring-slate-900/10 transition';

const WorkshopSignup = () => {
  const [plan, setPlan] = useState('professional');
  const [cycle, setCycle] = useState('monthly');
  const [discountCode, setDiscountCode] = useState('');
  const [catalog, setCatalog] = useState(PLANS);
  const [form, setForm] = useState({ workshop_name: '', owner_name: '', email: '', phone: '', password: '' });
  const [fieldErr, setFieldErr] = useState({});
  const [err, setErr] = useState('');
  const [dupe, setDupe] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);

  const yearly = cycle === 'yearly';
  const set = (k) => (e) => {
    const v = k === 'phone' ? e.target.value.replace(/[^\d+\s-]/g, '') : e.target.value;
    setForm((s) => ({ ...s, [k]: v }));
    setFieldErr((s) => ({ ...s, [k]: '' }));
  };

  // What the server's validation errors mean in plain English, by field.
  const FRIENDLY = {
    email: 'Please enter a valid email address, like you@workshop.com',
    workshop_name: 'Please enter your workshop name (at least 2 characters).',
    owner_name: 'Please enter your name (at least 2 characters).',
    password: 'Please use a password of at least 8 characters.',
    phone: 'Please enter a valid mobile number.',
  };

  const validate = () => {
    const e = {};
    if (form.workshop_name.trim().length < 2) e.workshop_name = FRIENDLY.workshop_name;
    if (form.owner_name.trim().length < 2) e.owner_name = FRIENDLY.owner_name;
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) e.email = FRIENDLY.email;
    const digits = form.phone.replace(/\D/g, '');
    if (form.phone && (digits.length < 10 || digits.length > 12)) e.phone = FRIENDLY.phone;
    if (form.password.length < 8) e.password = FRIENDLY.password;
    return e;
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr(''); setDupe('');
    const bad = validate();
    setFieldErr(bad);
    if (Object.keys(bad).length) return;

    setBusy(true);
    try {
      await axios.post(`${API}/signup`, {
        ...form,
        workshop_name: form.workshop_name.trim(),
        owner_name: form.owner_name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.replace(/\D/g, ''),
        plan,
        billing_cycle: cycle,
        discount_code: discountCode.trim(),
      }, { withCredentials: true });
      window.location.href = '/admin/subscription?welcome=1';
    } catch (e2) {
      const d = e2?.response?.data?.detail;
      if (typeof d === 'string') {
        // The one server-side error a human can act on: email already in use.
        if (/already exists/i.test(d)) {
          setDupe(true);
          setFieldErr({ email: 'This email already has a Driveline account.' });
        } else {
          setErr(d);
        }
      } else if (Array.isArray(d)) {
        // Pydantic sends raw messages like "String should have at least 2
        // characters" — map them back onto the field and say it in plain words.
        const mapped = {};
        d.forEach((item) => {
          const key = item?.loc?.[item.loc.length - 1];
          if (FRIENDLY[key]) mapped[key] = FRIENDLY[key];
        });
        setFieldErr(mapped);
        if (!Object.keys(mapped).length) setErr('Please check the details above and try again.');
      } else {
        setErr('Could not create your account. Please try again, or call us on +91 99161 20561.');
      }
      setBusy(false);
    }
  };

  const inputCls = (k) => `${field} ${fieldErr[k] ? 'border-red-400 focus:border-red-500 focus:ring-red-500/10' : ''}`;
  const FieldError = ({ k }) => (fieldErr[k]
    ? <p className="text-xs text-red-600 mt-1.5" data-testid={`signup-error-${k}`}>{fieldErr[k]}</p>
    : null);

  // The packages on sale are set in the owner console, so read them from the
  // server; the static list above is only the fallback if that call fails.
  useEffect(() => {
    let alive = true;
    axios.get(`${API}/plans`).then(({ data }) => {
      if (!alive || !data?.plans?.length) return;
      const labels = Object.fromEntries((data.modules || []).map((m) => [m.key, m.label]));
      const live = data.plans.filter((p) => !p.archived).map((p, i) => ({
        key: p.key,
        name: p.name,
        price: p.price_monthly,
        yearly: p.price_yearly,
        featured: i === 1,
        blurb: p.tagline || '',
        points: (p.features || []).map((f) => labels[f] || f).slice(0, 7),
      }));
      setCatalog(live);
      setPlan((prev) => (live.some((p) => p.key === prev) ? prev : (live[1] || live[0]).key));
    }).catch(() => { /* keep the fallback list */ });
    return () => { alive = false; };
  }, []);

  return (
    <div className="driveline-scope min-h-screen bg-[#FAFAF8] text-slate-900">
      <div className="absolute top-5 left-5">
        <Link to="/" className="inline-flex items-center gap-2 text-slate-500 hover:text-slate-900 text-sm" data-testid="signup-back">
          <ArrowLeft size={16} /> Back to site
        </Link>
      </div>

      <div className="max-w-6xl mx-auto px-5 sm:px-8 pt-20 pb-16">
        <div className="text-center mb-10 sm:mb-14">
          <img src="/brand/driveline-logo.png" alt="Driveline" className="mx-auto w-[13rem] sm:w-[17rem] h-auto" />
          <h1 className="mt-7 text-3xl sm:text-4xl font-semibold tracking-tight">Run your workshop on Driveline</h1>
          <p className="mt-3 text-base text-slate-500 max-w-xl mx-auto">
            Estimates, invoices, inspections, inventory and staff — in one place.
            Free for 14 days. No card needed to start.
          </p>
        </div>

        <div className="flex flex-col items-center gap-2 mb-7">
          <div className="inline-flex p-1 rounded-full bg-slate-200/70 border border-slate-200" data-testid="signup-cycle-toggle">
            {[{ k: 'monthly', l: 'Monthly' }, { k: 'yearly', l: 'Yearly' }].map((o) => (
              <button
                key={o.k}
                type="button"
                onClick={() => setCycle(o.k)}
                className={`px-6 py-2 rounded-full text-sm font-semibold transition-colors ${
                  cycle === o.k ? 'bg-slate-900 text-white' : 'text-slate-500 hover:text-slate-900'}`}
                data-testid={`signup-cycle-${o.k}`}
              >
                {o.l}
              </button>
            ))}
          </div>
          <p className="text-xs font-medium text-teal-700" data-testid="signup-cycle-hint">
            {yearly ? 'Two months free — you pay for 10, you get 12' : 'Pay yearly and two months are free'}
          </p>
        </div>

        <div className={`grid gap-5 mb-12 ${catalog.length > 3 ? 'lg:grid-cols-4' : 'lg:grid-cols-3'}`} data-testid="signup-plans">
          {catalog.map((p) => {
            const active = plan === p.key;
            return (
              <button
                type="button"
                key={p.key}
                onClick={() => setPlan(p.key)}
                className={`text-left rounded-2xl border p-6 transition-[border-color,box-shadow,transform] ${
                  active ? 'border-slate-900 bg-white shadow-[0_20px_50px_-28px_rgba(15,23,42,0.45)] -translate-y-0.5'
                         : 'border-slate-200 bg-white/70 hover:border-slate-400'}`}
                data-testid={`signup-plan-${p.key}`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-lg font-semibold">{p.name}</span>
                  {p.featured && (
                    <span className="text-[10px] uppercase tracking-wider bg-red-500/10 text-red-600 px-2 py-0.5 rounded-full">
                      Most popular
                    </span>
                  )}
                </div>
                <div className="mt-3 flex items-baseline gap-1">
                  <span className="text-3xl font-bold tabular-nums" data-testid={`signup-price-${p.key}`}>
                    ₹{(yearly ? p.yearly : p.price).toLocaleString('en-IN')}
                  </span>
                  <span className="text-sm text-slate-400">{yearly ? '/year' : '/month'}</span>
                </div>
                <p className="mt-0.5 text-xs text-teal-700 font-medium h-4" data-testid={`signup-saving-${p.key}`}>
                  {yearly ? `Saves ₹${(p.price * 12 - p.yearly).toLocaleString('en-IN')} a year` : ''}
                </p>
                <p className="mt-1.5 text-sm text-slate-500">{p.blurb}</p>
                <ul className="mt-4 space-y-1.5">
                  {p.points.map((pt) => (
                    <li key={pt} className="flex gap-2 text-sm text-slate-600">
                      <Check size={15} className="text-teal-600 shrink-0 mt-0.5" /> {pt}
                    </li>
                  ))}
                </ul>
              </button>
            );
          })}
        </div>

        <form
          onSubmit={submit}
          noValidate
          className="max-w-xl mx-auto bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 shadow-[0_24px_60px_-32px_rgba(15,23,42,0.35)] space-y-4"
          data-testid="signup-form"
        >
          <h2 className="text-lg font-semibold">Create your workshop account</h2>
          <div>
            <input className={inputCls('workshop_name')} placeholder="Workshop name" value={form.workshop_name}
                   onChange={set('workshop_name')} data-testid="signup-workshop-name" />
            <FieldError k="workshop_name" />
          </div>
          <div>
            <input className={inputCls('owner_name')} placeholder="Your name" value={form.owner_name}
                   onChange={set('owner_name')} data-testid="signup-owner-name" />
            <FieldError k="owner_name" />
          </div>
          <div>
            <input className={inputCls('email')} type="email" placeholder="Work email" autoComplete="email"
                   value={form.email} onChange={set('email')} data-testid="signup-email" />
            <FieldError k="email" />
            {dupe && (
              <p className="text-xs text-slate-500 mt-1.5" data-testid="signup-dupe-hint">
                <Link to="/admin/login" className="text-teal-700 underline font-medium">Sign in instead</Link>
                {' '}or use a different email for this workshop.
              </p>
            )}
          </div>
          <div>
            <input className={inputCls('phone')} type="tel" inputMode="numeric" placeholder="Mobile number (optional)"
                   value={form.phone} onChange={set('phone')} data-testid="signup-phone" />
            <FieldError k="phone" />
          </div>
          <div>
            <div className="relative">
              <input className={`${inputCls('password')} pr-12`} type={showPw ? 'text' : 'password'}
                     placeholder="Create a password (min 8 characters)"
                     autoComplete="new-password" value={form.password} onChange={set('password')}
                     data-testid="signup-password" />
              <button type="button" onClick={() => setShowPw((s) => !s)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                      aria-label={showPw ? 'Hide password' : 'Show password'}
                      data-testid="signup-password-toggle">
                {showPw ? <EyeOff size={17} /> : <Eye size={17} />}
              </button>
            </div>
            <FieldError k="password" />
          </div>

          <SignupDiscount code={discountCode} onCode={setDiscountCode} fieldCls={field} />

          {err && (
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-3 py-2" data-testid="signup-error">
              {err}
            </div>
          )}

          <button
            type="submit"
            disabled={busy}
            className="w-full inline-flex items-center justify-center gap-2 bg-slate-900 hover:bg-slate-800 disabled:bg-slate-400 text-white font-semibold py-3.5 rounded-xl transition-colors"
            data-testid="signup-submit"
          >
            {busy && <Loader2 className="animate-spin" size={17} />}
            {busy ? 'Setting up…' : `Start 14-day free trial`}
          </button>
          <p className="text-xs text-slate-400 text-center" data-testid="signup-choice-summary">
            You picked <b className="text-slate-600">{catalog.find((p) => p.key === plan)?.name}</b>,
            billed <b className="text-slate-600">{yearly ? 'yearly' : 'monthly'}</b>
            {yearly ? ' (two months free)' : ''}.
            You can change either any time — nothing is charged during the trial.
          </p>
          <p className="text-xs text-slate-400 text-center">
            By starting a trial you agree to our{' '}
            <Link to="/driveline/terms" className="underline" data-testid="signup-terms-link">Terms</Link>,{' '}
            <Link to="/driveline/privacy" className="underline" data-testid="signup-privacy-link">Privacy Policy</Link> and{' '}
            <Link to="/driveline/refund-policy" className="underline" data-testid="signup-refund-link">Refund Policy</Link>.
          </p>
          <p className="text-xs text-slate-400 text-center">
            Already have an account? <Link to="/admin/login" className="underline" data-testid="signup-login-link">Sign in</Link>
          </p>
        </form>
      </div>
    </div>
  );
};

export default WorkshopSignup;
